import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
} from 'reactflow'
import 'reactflow/dist/style.css'

import Header from '../components/layout/Header'
import NodePalette from '../components/workflow/NodePalette'
import MissionControl from '../components/workflow/MissionControl'
import { useWorkflowStore } from '../stores/workflowStore'

// Custom node types
import AgentNode from '../components/nodes/AgentNode'
import ToolNode from '../components/nodes/ToolNode'
import TaskNode from '../components/nodes/TaskNode'
import StartNode from '../components/nodes/StartNode'

const nodeTypes = {
  agent: AgentNode,
  tool: ToolNode,
  task: TaskNode,
  start: StartNode,
}

const initialNodes: Node[] = [
  {
    id: 'start-1',
    type: 'start',
    position: { x: 100, y: 100 },
    data: { label: 'Start' },
  },
  {
    id: 'agent-sample',
    type: 'agent',
    position: { x: 300, y: 80 },
    data: { 
      label: 'Sample Agent',
      config: {
        role: 'Research Assistant',
        goal: 'Gather and analyze information to help with tasks',
        backstory: 'An AI assistant specialized in research and data analysis',
        model: 'gpt-4',
        tools: []
      }
    },
  },
]

const initialEdges: Edge[] = [
  {
    id: 'start-to-sample',
    source: 'start-1',
    target: 'agent-sample',
    type: 'default',
  }
]

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const [showMissionControl, setShowMissionControl] = useState(false)
  const [workflowName] = useState('Untitled Workflow')
  
  const { 
    saveWorkflow, 
    executeWorkflow, 
    isExecuting,
    executionLogs 
  } = useWorkflowStore()

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()

      const type = event.dataTransfer.getData('application/reactflow')
      const label = event.dataTransfer.getData('application/reactflow/label')

      if (typeof type === 'undefined' || !type) {
        return
      }

      const position = {
        x: event.clientX - 250, // Adjust for sidebar width
        y: event.clientY - 100, // Adjust for header height
      }

      const newNode: Node = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: { 
          label,
          config: getDefaultConfig(type)
        },
      }

      setNodes((nds) => nds.concat(newNode))
    },
    [setNodes]
  )

  const getDefaultConfig = (type: string) => {
    switch (type) {
      case 'agent':
        return {
          role: 'Assistant',
          goal: 'Help with tasks',
          backstory: 'An AI assistant ready to help',
          model: 'gpt-4',
          tools: []
        }
      case 'tool':
        return {
          name: 'Web Search',
          description: 'Search the internet for information',
          parameters: {}
        }
      case 'task':
        return {
          description: 'Complete a specific task',
          expectedOutput: 'Task completion confirmation',
          agent: null
        }
      default:
        return {}
    }
  }

  const handleSave = async () => {
    try {
      await saveWorkflow({
        name: workflowName,
        nodes,
        edges,
        createdAt: new Date(),
        updatedAt: new Date()
      })
      // Show success notification
      console.log('Workflow saved successfully')
    } catch (error) {
      console.error('Failed to save workflow:', error)
    }
  }

  const handleKickoff = async () => {
    if (nodes.length <= 1) {
      alert('Please add some agents and tasks before starting the workflow.')
      return
    }

    try {
      setShowMissionControl(true)
      const workflowId = await executeWorkflow({
        name: workflowName,
        nodes,
        edges,
        createdAt: new Date(),
        updatedAt: new Date()
      })
      
      // Navigate to kickoff page for real-time monitoring
      navigate(`/kickoff/${workflowId}`)
    } catch (error) {
      console.error('Failed to start workflow:', error)
      alert('Failed to start workflow. Please try again.')
    }
  }

  const handlePause = () => {
    // Implement pause functionality
    console.log('Pausing workflow...')
  }

  const handleStop = () => {
    // Implement stop functionality
    console.log('Stopping workflow...')
    setShowMissionControl(false)
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <Header
        workflowName={workflowName}
        showWorkflowControls={true}
        isExecuting={isExecuting}
        onSave={handleSave}
        onKickoff={handleKickoff}
        onPause={handlePause}
        onStop={handleStop}
      />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Node Palette */}
        <NodePalette />
        
        {/* Main Canvas Area */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            fitView
            className="canvas-area"
          >
            <Controls className="react-flow-controls" />
            <MiniMap 
              className="react-flow-minimap"
              nodeColor={(node) => {
                switch (node.type) {
                  case 'agent': return '#3b82f6'
                  case 'tool': return '#f59e0b'
                  case 'task': return '#8b5cf6'
                  case 'start': return '#1e3a8a'
                  default: return '#6b7280'
                }
              }}
            />
            <Background 
              variant={BackgroundVariant.Dots} 
              gap={20} 
              size={1}
              className="grid-background"
            />
          </ReactFlow>
          
          {/* Empty State */}
          {nodes.length <= 1 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-400 dark:text-gray-500">
                <div className="mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium mb-2">Drag components to build your workflow</h3>
                <p className="text-sm">Start by dragging agents, tools, and tasks from the left panel</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Sidebar - Mission Control */}
        {showMissionControl && (
          <MissionControl
            isVisible={showMissionControl}
            onClose={() => setShowMissionControl(false)}
            logs={executionLogs}
            isExecuting={isExecuting}
          />
        )}
      </div>
      
      {/* Bottom Status Bar */}
      <div className="h-8 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between px-6 text-xs text-gray-600 dark:text-gray-400">
        <div className="flex items-center space-x-4">
          <span>Ready</span>
          <span>•</span>
          <span>{nodes.filter(n => n.type === 'agent').length} Agents, {nodes.filter(n => n.type === 'tool').length} Tools, {nodes.filter(n => n.type === 'task').length} Tasks</span>
        </div>
        <div className="flex items-center space-x-4">
          <span>Zoom: 100%</span>
          <span>•</span>
          <span>Grid: On</span>
          <span>•</span>
          <span>Auto-save: Enabled</span>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
